import React from 'react';
import { BootstrapMultiSelect } from 'components';
import dateformat from 'utils/dateformat';
import { FaLink } from 'react-icons/fa';
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  FloatingLabel,
} from 'react-bootstrap';
import PermissionsGrid from './PermissionsGrid';
import SguAccessSection from './SguAccessSection';
import { hasSguCompanyLink as checkSguCompanyLink } from './sguAccessUtils';
import { formatCpfOrCnpj } from 'utils/cpfCnpj';
import { getThemeFormControlClass } from 'utils/tableTheme';
import './Forms.css';
import './PermissionTableCard.css';

const Forms = (props) => {
  const {
    user,
    permission,
    fieldChanged,
    phoneChanged,
    saveUser,
    setUser,
    setValueForm,
    setValueFormSenhas,
    theme,
    companies
  } = props;


  const ACCESS_TYPES = {
    NO_ACCESS: 'no_access',
    COOPERATIVE_MANAGER: 'cooperative_manager',
    UNIT_MANAGER: 'unit_manager',
    MULTI_UNIT_MANAGER: 'multi_unit_manager'
  };

  const companyById = (Array.isArray(companies) ? companies : []).reduce((acc, company) => {
    if (company?._id) {
      acc[company._id] = company;
    }
    return acc;
  }, {});

  const companyByName = (Array.isArray(companies) ? companies : []).reduce((acc, company) => {
    if (company?.name && company?._id) {
      acc[company.name] = company;
    }
    return acc;
  }, {});

  const cooperativeOptions = (Array.isArray(companies) ? companies : [])
    .filter((item) => item?.typeCompany === true)
    .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'pt-BR'));

  const unitOptions = (Array.isArray(companies) ? companies : [])
    .filter((item) => item?.typeCompany !== true)
    .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'pt-BR'));

  const unitNameOptions = unitOptions
    .map((item) => item?.name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const handleAccessTypeChange = (event) => {
    const value = event.target.value;
    setUser({
      ...user,
      accessType: value,
      managerId: '',
      allowedUnitIds: [],
      sguTrial: null
    });
  };

  const handleSingleManagerSelection = (valueOrUpdater) => {
    const currentName = companyById[user?.managerId]?.name;
    const prev = currentName ? [currentName] : [];
    const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
    const limited = Array.isArray(next) ? next.slice(-1) : [];
    const selectedManagerId = companyByName[limited[0]]?._id || '';

    setUser({
      ...user,
      managerId: selectedManagerId,
      allowedUnitIds: [],
      sguTrial: selectedManagerId ? user.sguTrial : null
    });
  };

  const isSingleManagerType =
    user?.accessType === ACCESS_TYPES.COOPERATIVE_MANAGER ||
    user?.accessType === ACCESS_TYPES.UNIT_MANAGER;
  const shouldShowManagementBinding =
    isSingleManagerType || user?.accessType === ACCESS_TYPES.MULTI_UNIT_MANAGER;
  const singleManagerOptions = (
    user?.accessType === ACCESS_TYPES.COOPERATIVE_MANAGER ? cooperativeOptions : unitOptions
  ).map((item) => item?.name).filter(Boolean);
  const selectedSingleManagerFromUser = companyById[user?.managerId]?.name ? [companyById[user.managerId].name] : [];

  const selectedUnitsFromUser = (Array.isArray(user?.allowedUnitIds) ? user.allowedUnitIds : [])
    .map((id) => companyById[id]?.name)
    .filter(Boolean);

  const hasSguCompanyLink = checkSguCompanyLink(
    user?.accessType,
    user?.managerId,
    user?.allowedUnitIds
  );

  const handleMultiUnitSelection = (valueOrUpdater) => {
    const prevSelected = (Array.isArray(user?.allowedUnitIds) ? user.allowedUnitIds : [])
      .map((id) => companyById[id]?.name)
      .filter(Boolean);
    const nextNames = typeof valueOrUpdater === 'function' ? valueOrUpdater(prevSelected) : valueOrUpdater;
    const nextIds = (Array.isArray(nextNames) ? nextNames : [])
      .map((unitName) => companyByName[unitName]?._id)
      .filter(Boolean);

    setUser({
      ...user,
      managerId: '',
      allowedUnitIds: Array.from(new Set(nextIds)),
      sguTrial: nextIds.length > 0 ? user.sguTrial : null
    });
  };

  return (
    <Card className={`forms-card mb-3 mt-3 p-3 ${theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'}`}>
      <Row className="g-2">
        <Col md>
          <FloatingLabel controlId="floatingInput" label="Nome" className="mb-3">
            <Form.Control
              type="text"
              name="nome"
              placeholder="Nome"
              value={user?.name || ''}
              onChange={(event) =>
                fieldChanged(event, 'name', event.target.value)
              }
              disabled={!permission?.users?.write}
            />
          </FloatingLabel>
        </Col>
        <Col md>
          <FloatingLabel controlId="floatingInput" label="E-mail" className="mb-3">
            <Form.Control
              type="text"
              name="email"
              placeholder="E-mail"
              value={user?.email || ''}
              onChange={(event) =>
                fieldChanged(event, 'email', event.target.value.trim().toLowerCase())
              }
              disabled={!permission?.users?.write}
            />
          </FloatingLabel>
        </Col>
      </Row>

      <Row className="g-2">
        <Col md>
          <FloatingLabel controlId="floatingInput" label="CPF/CNPJ" className="mb-3">
            <Form.Control
              type="text"
              name="cpfcnpj"
              placeholder="CPF/CNPJ"
              value={user?.cpfcnpj || ''}
              onChange={(event) =>
                fieldChanged(event, 'cpfcnpj', formatCpfOrCnpj(event.target.value))
              }
              maxLength={18}
              disabled={!permission?.users?.write}
            />
          </FloatingLabel>
        </Col>

        <Col md>
          <FloatingLabel controlId="floatingInput" label="Empresa" className="mb-3">
            <Form.Control
              type="text"
              name="company"
              placeholder="Empresa"
              value={user?.company || ''}
              onChange={(event) =>
                fieldChanged(event, 'company', event.target.value)
              }
              disabled={!permission?.users?.write}
            />
          </FloatingLabel>
        </Col>
      </Row>

      <Row className='g-2'>
        <Col md>
          <FloatingLabel controlId="floatingInput" label="Fone" className="mb-0">
            <Form.Control
              type="Fone"
              name="fone"
              placeholder="Fone"
              value={user?.fone || ''}
              onChange={(event) =>
                phoneChanged(event, 'fone', event.target.value)
              }
              disabled={!permission?.users?.write}
            />
          </FloatingLabel>
        </Col>

        <Col md>
          <FloatingLabel controlId="floatingCreatedAt" label="Cadastrado em" className="mb-0">
            <Form.Control
              type="text"
              name="createdAt"
              placeholder="Cadastrado em"
              value={dateformat(user?.createdAt, true)}
              readOnly
              disabled
            />
          </FloatingLabel>
        </Col>

        <Col md>
          <FloatingLabel controlId="floatingLastLogin" label="Último login" className="mb-0">
            <Form.Control
              type="text"
              name="lastLogin"
              placeholder="Último login"
              value={
                user?.lastLogin
                  ? `${dateformat(user.lastLogin, true)}${user?.app ? ` (${user.app})` : ''}`
                  : '--'
              }
              readOnly
              disabled
            />
          </FloatingLabel>
        </Col>
      </Row>

      <Row className="g-2 mt-2">
        <Col md={12}>
          <Form.Check
            type="checkbox"
            id="monthlyReportEmail"
            label="Receber relatório mensal por e-mail"
            disabled={!permission?.users?.write}
            checked={Boolean(user?.monthlyReportEmail)}
            onChange={(event) =>
              fieldChanged(event, 'monthlyReportEmail', event.target.checked)
            }
          />
        </Col>
      </Row>


      {permission?.users?.read && permission?.users?.write ? (
        <>
          <div
            className={`user-binding-section mt-4 ${theme === 'dark' ? 'user-binding-section--dark' : ''}`}
          >
            <div className="user-binding-section__header">
              <div className="user-binding-section__icon" aria-hidden="true">
                <FaLink />
              </div>
              <div>
                <h5 className="user-binding-section__title mb-1">Vínculo com unidade</h5>
                <p className="user-binding-section__subtitle mb-0">
                  Selecione o tipo de gestor e vincule o usuário à cooperativa ou unidade desejada.
                </p>
              </div>
            </div>

            <div className="user-binding-section__body">
              <Row className="g-2">
                <Col md={12}>
                  <FloatingLabel
                    controlId="floatingAccessType"
                    label="Tipo de gestor"
                    className={`mb-0 ${theme === 'dark' ? 'text-light' : 'text-dark'}`}
                  >
                    <Form.Select
                      value={user?.accessType || ''}
                      onChange={handleAccessTypeChange}
                      disabled={!permission?.users?.write}
                      className={getThemeFormControlClass(theme)}
                    >
                      <option value="">Selecione o tipo de gestor</option>
                      <option value={ACCESS_TYPES.NO_ACCESS}>Sem acesso</option>
                      <option value={ACCESS_TYPES.COOPERATIVE_MANAGER}>Gestor de Cooperativa</option>
                      <option value={ACCESS_TYPES.UNIT_MANAGER}>Gestor de Unidade</option>
                      <option value={ACCESS_TYPES.MULTI_UNIT_MANAGER}>Gestor de 2 ou mais Unidades</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
              </Row>

              {shouldShowManagementBinding && (
                <div className={`user-binding-panel mt-3 ${theme === 'dark' ? 'user-binding-panel--dark' : ''}`}>
                  <Row className="mb-2">
                    <Col>
                      <h6 className="mb-1">Selecionar vínculo</h6>
                      <small className={theme === 'dark' ? 'text-light opacity-75' : 'text-muted'}>
                        {isSingleManagerType
                          ? 'Escolha uma cooperativa ou unidade. O vínculo é aplicado automaticamente.'
                          : 'Escolha uma ou mais unidades. O vínculo é aplicado automaticamente.'}
                      </small>
                    </Col>
                  </Row>
                  <Row className="g-2 mt-1">
                    {isSingleManagerType ? (
                      <Col md={12}>
                        <BootstrapMultiSelect
                          hiddenOptions={true}
                          label={user?.accessType === ACCESS_TYPES.COOPERATIVE_MANAGER ? 'Cooperativas' : 'Unidades'}
                          options={singleManagerOptions}
                          selectedEquipments={selectedSingleManagerFromUser}
                          setSelectedEquipments={handleSingleManagerSelection}
                        />
                      </Col>
                    ) : (
                      <Col md={12}>
                        <BootstrapMultiSelect
                          hiddenOptions={true}
                          label="Unidades"
                          options={unitNameOptions}
                          selectedEquipments={selectedUnitsFromUser}
                          setSelectedEquipments={handleMultiUnitSelection}
                        />
                      </Col>
                    )}
                  </Row>

                  {user?.accessType === ACCESS_TYPES.MULTI_UNIT_MANAGER &&
                    (Array.isArray(user?.allowedUnitIds) ? user.allowedUnitIds : []).length === 0 ? (
                    <small className={`d-block mt-2 ${theme === 'dark' ? 'text-light opacity-75' : 'text-muted'}`}>
                      Nenhuma unidade vinculada.
                    </small>
                  ) : null}

                  {user?.accessType === ACCESS_TYPES.MULTI_UNIT_MANAGER &&
                    (Array.isArray(user?.allowedUnitIds) ? user.allowedUnitIds : []).length > 0 ? (
                    <div className="d-flex flex-column gap-2 mt-3">
                      {(Array.isArray(user?.allowedUnitIds) ? user.allowedUnitIds : [])
                        .slice()
                        .sort((a, b) => {
                          const nameA = companyById[a]?.name || '';
                          const nameB = companyById[b]?.name || '';
                          return nameA.localeCompare(nameB, 'pt-BR');
                        })
                        .map((companyId) => (
                          <div
                            key={companyId}
                            className={`user-binding-item ${theme === 'dark' ? 'user-binding-item--dark' : ''}`}
                          >
                            <span>
                              {companyById[companyId]?.name || 'Unidade não encontrada'}
                            </span>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={!permission?.users?.write}
                              onClick={() => {
                                const currentIds = Array.isArray(user?.allowedUnitIds) ? user.allowedUnitIds : [];
                                const nextIds = currentIds.filter((id) => id !== companyId);
                                setUser({
                                  ...user,
                                  allowedUnitIds: nextIds,
                                  sguTrial: nextIds.length > 0 ? user.sguTrial : null
                                });
                              }}
                            >
                              Remover
                            </Button>
                          </div>
                        ))}
                    </div>
                  ) : null}

                  {hasSguCompanyLink ? (
                    <SguAccessSection
                      user={user}
                      setUser={setUser}
                      theme={theme}
                      canEdit={permission?.users?.write}
                      companyById={companyById}
                      isSingleManagerType={isSingleManagerType}
                    />
                  ) : (
                    <p className={`mb-0 mt-3 small ${theme === 'dark' ? 'text-light opacity-75' : 'text-muted'}`}>
                      Após vincular a empresa, você poderá definir o acesso ao SGU (assinante ou trial).
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={`user-permissions-divider ${theme === 'dark' ? 'user-permissions-divider--dark' : ''}`}>
            <span>Permissões e configurações avançadas</span>
          </div>

          <PermissionsGrid
            user={user}
            setUser={setUser}
            permission={permission}
            theme={theme}
          />

        </>
      ) : null}

      <div className={`permissions-save-bar ${theme === 'dark' ? 'permissions-save-bar--dark' : ''}`}>
        <Button
          disabled={!permission?.users?.write}
          onClick={saveUser}
          variant="success"
          className="btn-theme-primary"
        >
          Salvar alterações
        </Button>
      </div>

      <hr />

      <Row>
        <Col>
          <Button
            className='mb-2 me-2'
            disabled={!permission?.users?.read}
            onClick={() => {
              setValueForm(true)
              setValueFormSenhas(false)
            }}
            variant="success"
          >
            Abrir Ultimas Ativações
          </Button>
          <Button
            className='mb-2'
            disabled={!permission?.users?.read}
            onClick={() => {
              setValueForm(false)
              setValueFormSenhas(true)
            }}
            variant="success"
          >
            Exibir Senhas Geradas
          </Button>
        </Col>
      </Row>
    </Card>
  )
}

export default Forms;
